import { useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router";
import { lookupEnvironmentFilesByIds } from "@/lib/api";
import { fileTabIdsFromUrl } from "@/lib/file-tab-url";
import { selectEffectiveEnvironmentId, useChatStore } from "@/stores/useChatStore";
import { useLayoutStore } from "@/stores/useLayoutStore";
import { useToastStore } from "@/stores/useToastStore";

/**
 * 開いているファイルタブを URL クエリ (`files=<id1,id2,...>&file=<アクティブID>`) と同期する。
 *
 * - チャット読み込み時、URL の `files` に列挙されたファイルがワークスペースに現れたら
 *   同じ順序ですべて開き、`file` が指すファイルをアクティブ化する (リロードやディープリンク
 *   で全タブが復元される)。`files` が無く `file` のみの旧形式 URL も 1 タブとして復元する。
 * - 開いているファイルタブの一覧・アクティブなタブが変わったら URL を更新する。
 *
 * 何も描画しない。`ChatUrlSync` と同じく AppLayout 内にマウントする。
 */
export function FileUrlSync() {
	const [searchParams, setSearchParams] = useSearchParams();
	const { chatId: routeChatId } = useParams<{ chatId: string }>();
	const chatId = useChatStore((s) => s.chatId);
	const environmentId = useChatStore(selectEffectiveEnvironmentId);
	const filesReady = useChatStore((s) => s.environmentFileChildren !== null);
	const openFileIds = useChatStore((s) => s.openFileIds);
	const activeFileId = useChatStore((s) => s.activeFileId);

	// ルートの chatId とストアの chatId が一致するまでは URL を読み書きしない。
	// チャット切り替え中はルートが先に新チャットへ進む一方ストアは旧チャットのままで、
	// この間に書き込むと旧ファイル ID を新しいルートへ復活させてしまい URL がちらつく。
	const inSync = routeChatId != null && routeChatId === chatId;

	// 復元が完了 (または対象なしと判断) したチャット。これが現在の chatId と一致するまでは
	// store → URL の書き込みを抑止し、復元前に URL のパラメータを消さないようにする。
	const restoredChatRef = useRef<string | null>(null);
	/** 照合 (lookup-ids) が進行中のチャット。同じ復元を重複起動しないための目印。 */
	const restoringChatRef = useRef<string | null>(null);

	// URL → ストア: ルートの取得が済んだら、URL のタブ一覧を索引と照合して復元する。
	useEffect(() => {
		if (!inSync || restoredChatRef.current === chatId) return;
		const activeParam = searchParams.get("file");
		const ids = fileTabIdsFromUrl(searchParams);
		if (ids.length === 0) {
			restoredChatRef.current = chatId;
			return;
		}
		// ツリーの初回取得を待つ (環境が定まる前に照合しない)。
		if (!filesReady || environmentId === null) return;
		// 照合は非同期なので、完了するまで同じチャットの復元を重ねて走らせない。
		if (restoringChatRef.current === chatId) return;
		restoringChatRef.current = chatId;

		void (async () => {
			let files: Awaited<ReturnType<typeof lookupEnvironmentFilesByIds>>;
			try {
				files = await lookupEnvironmentFilesByIds(environmentId, ids);
			} catch (err) {
				restoringChatRef.current = null;
				useToastStore
					.getState()
					.show({ type: "error", message: "開いていたファイルを復元できませんでした。" });
				console.error("開いていたファイルの復元に失敗しました。", err);
				useLayoutStore.getState().finishFileTabRestore();
				throw err;
			}
			// 開く前に終えても、0 件でなければ同じ描画内で右ペインが開き直すためちらつかない。
			useLayoutStore.getState().finishFileTabRestore();
			const store = useChatStore.getState();
			// 照合中にチャットや環境が切り替わっていたら、切替後の状態へ持ち込まない。
			if (store.chatId !== chatId || selectEffectiveEnvironmentId(store) !== environmentId) return;

			const byId = new Map(files.map((file) => [file.id, file]));
			const existingIds = ids.filter((id) => byId.has(id));
			store.mergeOpenEnvironmentFileEntries(files);
			for (const id of existingIds) store.openFile(id);
			const activeId =
				activeParam && existingIds.includes(activeParam) ? activeParam : existingIds.at(-1);
			if (activeId) {
				store.openFile(activeId);
				const file = byId.get(activeId);
				if (file) store.selectEnvironmentFilesTreeNode(file.path);
			}
			// 復元し終えてから store → URL の書き戻しを解禁する (途中で URL を消さないため)。
			restoredChatRef.current = chatId;
		})();
	}, [inSync, chatId, environmentId, filesReady, searchParams]);

	// ストア → URL: 開いているタブ一覧とアクティブなタブを URL へ反映する (復元完了後のみ)。
	useEffect(() => {
		if (!inSync || restoredChatRef.current !== chatId) return;
		const nextFiles = openFileIds.length > 0 ? openFileIds.join(",") : null;
		if (nextFiles === searchParams.get("files") && activeFileId === searchParams.get("file")) {
			return;
		}
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				if (nextFiles) next.set("files", nextFiles);
				else next.delete("files");
				if (activeFileId) next.set("file", activeFileId);
				else next.delete("file");
				return next;
			},
			{ replace: true },
		);
	}, [inSync, chatId, openFileIds, activeFileId, searchParams, setSearchParams]);

	return null;
}
